<script setup lang="ts">
import type { PolicyDocumentAction } from '#layers/access/types/permissions/policy';
import type { ButtonProps } from '#layers/ui/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';

type RuleInformationType = 'none' | 'all' | 'custom';

export interface PolicyActionCellRuleInformation {
	type: RuleInformationType;
	hiddenFieldCount?: number;
	itemFilterConditionCount?: number;
	fieldFilterCount?: number;
}

interface PolicyActionCellProps {
	action: PolicyDocumentAction;
	collectionName: string;
	ruleInformation: PolicyActionCellRuleInformation;
	/** Disables the "Custom" access control when custom rules are unavailable. */
	customDisabled?: boolean;
	/** Explains why the "Custom" access control is disabled. */
	customDisabledTooltip?: string;
	/** Disables the whole access control group when the connector does not support this action. */
	disabled?: boolean;
	/** Explains why granting this action is disabled. */
	disabledTooltip?: string;
}

const {
	ruleInformation,
	customDisabled,
	customDisabledTooltip,
	action,
	collectionName,
} = defineProps<PolicyActionCellProps>();
const emit = defineEmits<{
	noAccess: [];
	allAccess: [];
	customAccess: [];
}>();

const { t } = useI18n();
const appConfig = useAppConfig();

const ruleType = computed<PolicyActionCellRuleInformation['type']>(() => ruleInformation.type);

const filterCount = computed(() => {
	return (ruleInformation?.itemFilterConditionCount ?? 0)
		+ (ruleInformation?.fieldFilterCount ?? 0);
});

const hiddenFieldCount = computed(() => {
	return ruleInformation?.hiddenFieldCount ?? 0;
});

// The reason an unsupported action is disabled lives on the indicator tooltip; the hover group is
// not rendered in that case, so these only describe the enabled controls.
const allTooltip = computed(() => getTooltipText('all', action, collectionName));

const noneTooltip = computed(() => getTooltipText('none', action, collectionName));

const customTooltip = computed(() =>
	customDisabled
		? customDisabledTooltip ?? t('policy.policy_rules.custom_gated_tooltip')
		: getTooltipText('custom', action, collectionName),
);

function getVariant(type: PolicyActionCellRuleInformation['type'], activeType: PolicyActionCellRuleInformation['type']) {
	if (type === activeType) return 'subtle';
	else return 'outline';
}

function getColor(type: PolicyActionCellRuleInformation['type'], activeType: PolicyActionCellRuleInformation['type'], activeColor: ButtonProps['color']) {
	if (type === activeType) return activeColor;
	else return 'neutral';
}

function getBindings(type: PolicyActionCellRuleInformation['type'], activeType: PolicyActionCellRuleInformation['type'], activeColor: ButtonProps['color']): any {
	const iconMap = { all: appConfig.ui.icons.check, none: 'lucide:ban', custom: 'lucide:settings-2' };
	return {
		color: getColor(type, activeType, activeColor),
		variant: getVariant(type, activeType),
		icon: iconMap[type],
	};
}

function getTooltipText(type: RuleInformationType, action: PolicyDocumentAction, collectionName: string) {
	if (type === 'all') {
		return t('policy.policy_rules.full_action_access_tooltip', { action: t(`policy.action.${action}`), collectionName });
	}

	if (type === 'none') {
		return t('policy.policy_rules.none_action_access_tooltip', { action: t(`policy.action.${action}`), collectionName });
	}

	// Custom type tooltip text
	if ((ruleInformation?.hiddenFieldCount ?? 0) > 0 && ruleInformation.fieldFilterCount === 0) {
		return t('policy.policy_rules.custom_action_access_tooltip_hidden_fields', { n: ruleInformation.hiddenFieldCount });
	}
	else if ((ruleInformation?.hiddenFieldCount ?? 0) === 0 && (ruleInformation?.fieldFilterCount ?? 0) > 0) {
		return t('policy.policy_rules.custom_action_access_tooltip_filters', { n: ruleInformation.fieldFilterCount });
	}
	else if ((ruleInformation?.hiddenFieldCount ?? 0) > 0 && (ruleInformation?.fieldFilterCount ?? 0) > 0) {
		return `${t('policy.policy_rules.custom_action_access_tooltip_hidden_fields', { n: ruleInformation.hiddenFieldCount })}, ${t('policy.policy_rules.custom_action_access_tooltip_filters', { n: ruleInformation.fieldFilterCount })}`;
	}
	else {
		return t('policy.policy_rules.custom_action_access_tooltip', { action: t(`policy.action.${action}`), collectionName });
	}
}
</script>

<template>
	<div class="relative flex flex-1 justify-center">
		<div class="group flex flex-1 items-center justify-center">
			<!-- Default state. When the action is unsupported this is all the cell shows: the current indicator, disabled, carrying the reason on its tooltip; the hover group never opens. -->
			<DTooltip :text="disabled ? disabledTooltip ?? '' : ''" :delay-duration="500">
				<div data-testid="indicator" class="flex items-center" :class="{ 'group-hover:opacity-0': !disabled }">
					<DButton
						v-if="ruleInformation.type === 'all'"
						:icon="appConfig.ui.icons.check"
						size="sm"
						variant="link"
						color="success"
						:disabled
					/>
					<DButton
						v-else-if="ruleInformation.type === 'none'"
						icon="lucide:ban"
						size="sm"
						variant="link"
						class="text-dimmed"
						:disabled
					/>
					<DButton
						v-if="ruleInformation.type === 'custom' && filterCount > 0"
						:icon="appConfig.ui.icons.filter"
						size="sm"
						variant="link"
						color="warning"
						:disabled
					/>
					<DButton
						v-if="ruleInformation.type === 'custom' && hiddenFieldCount > 0"
						icon="lucide:lock-keyhole"
						size="sm"
						variant="link"
						color="warning"
						:disabled
					/>
				</div>
			</DTooltip>

			<!-- Hover state (action buttons). Only offered when the connector supports the action. -->
			<DFieldGroup v-if="!disabled" class="absolute opacity-0 group-hover:opacity-100">
				<DTooltip :text="allTooltip" :delay-duration="500">
					<DButton
						v-bind="getBindings(ruleType, 'all', 'success')"
						:icon="appConfig.ui.icons.check"
						size="sm"
						@click="emit('allAccess')"
					/>
				</DTooltip>
				<DTooltip :text="noneTooltip" :delay-duration="500">
					<DButton
						v-bind="getBindings(ruleType, 'none', 'neutral')"
						icon="lucide:ban"
						size="sm"
						@click="emit('noAccess')"
					/>
				</DTooltip>

				<DTooltip :text="customTooltip" :delay-duration="500">
					<DButton
						v-bind="getBindings(ruleType, 'custom', 'warning')"
						icon="lucide:settings-2"
						size="sm"
						:disabled="customDisabled"
						@click="emit('customAccess')"
					/>
				</DTooltip>
			</DFieldGroup>
		</div>
	</div>
</template>
